import React, { ReactElement, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AxiosError, AxiosResponse } from 'axios';

import { PasswordRegisterApiResponse } from 'features/auth';
import { Page, Box, Typography, Flex, ButtonIcon, HelperText, Loading, Hr } from 'components';
import { request, createUrl, useFormikHandles, cpfMask, testCpf, dateMask, phoneMask } from 'app/utils';
import { useSchool } from 'features/school';
import Alert, { useAlert } from 'features/alert';
import { AuthInput2, AuthButton } from 'features/auth/components';
import styled from 'styled-components';

const ButtonShowPassword = styled(ButtonIcon)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 15px;
`;

const ResetPasswordView = (): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState<PasswordRegisterApiResponse>(undefined!);
  const { apiGatewayUrl, tenantUuid } = useSchool();
  const [showPassword, toggleShowPassword] = useState(false);
  const { recoveryTokenUuid } = useParams();
  const { setAlert, clearAlert } = useAlert();
  const navigate = useNavigate();

  const initialValues: Record<string, any> = {
    taxId: '',
    birthDate: '',
    password: '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      taxId: Yup.string()
        .required('Você precisa informar seu CPF.')
        .test('taxDocument', 'CPF informado está inválido.', (value) => testCpf(`${value}`)),

      birthDate: Yup.string()
        .required('Você precisa informar sua data de nascimento.')
        .matches(/^\d{2}[./-]\d{2}[./-]\d{4}$/, 'A data de nascimento informada está inválida.'),

      password: Yup.string().required('Você precisa informar uma senha de acesso.'),
      // .matches(
      //   /^(?=.*[A-Za-z])(?=.*\d)(?=.*[%?!@#$&*])[A-Za-z\d%?!@#$&*]{8,}$/,
      //   'Por segurança, sua senha deve conter no mínimo 8 caracteres sendo pelo menos 1 letra maiúscula, 1 letra minúscula, 1 número e um caractere especial (%?!@#$&*).'
      // ),
    }),
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const { password, taxId, birthDate } = values;

      const birthDateSanitized = birthDate.split('/');

      request(
        {
          url: '/api/v1/User/PasswordRegister',
          method: 'post',
          baseURL: apiGatewayUrl,
          data: {
            tenantUuid,
            recoveryToken: recoveryTokenUuid,
            password,
            taxId: taxId && taxId.replace(/\D/g, ''),
            birthOrFoundation: `${birthDateSanitized[2]}-${birthDateSanitized[1]}-${birthDateSanitized[0]}`,
          },
        },
        true
      )
        .then(() => {
          setSubmitting(false);
          resetForm();

          navigate(createUrl('login'));

          setAlert({
            type: 'success',
            message: 'Seu cadastro foi finalizado com sucesso.',
          });
        })
        .catch(
          (
            error: AxiosError<{
              authenticationResult: number;
              message: string;
            }>
          ) => {
            setSubmitting(false);

            let message = '<strong>Ocorreu um erro ao finalizar seu cadastro.</strong><br />Por favor, tente novamente mais tarde.';

            if (error?.response?.data?.message) {
              message = error?.response?.data?.message;
            }

            setAlert({
              type: 'error',
              message,
            });
          }
        );
    },
  });

  const { formikHandleFocus, formikHandleBlur, formikHandleChange } = useFormikHandles(formik);

  const fetch = async () => {
    request(
      {
        url: `/api/v1/User/PasswordRegister/${recoveryTokenUuid}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      },
      true
    )
      .then((response: AxiosResponse<PasswordRegisterApiResponse>) => {
        setLoading(false);
        setUserDetails(response.data);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetch();

    return () => {
      clearAlert(); // Clear Alert
    };
  }, []);

  if (loading) return <Loading label="Por favor, aguarde." />;

  return userDetails ? (
    <Page title="Finalizar cadastro">
      {formik.isSubmitting && <Loading alpha />}

      <Alert />

      <form onSubmit={formik.handleSubmit}>
        <Flex flexDirection="column">
          <Flex flexDirection="column" px={{ m: 30, d: 45 }} pt={{ m: 30, d: 45 }}>
            <Typography type="type6" color="authLabelColor" fontWeight="bold">
              Olá, {userDetails.name}
            </Typography>

            <Flex flexDirection="column" mt={2} mb={3}>
              <Typography lineHeight="1.2" type="type2" color="authLabelColor">
                Finalize seu cadastro e tenha acesso à plataforma.
              </Typography>

              <Typography lineHeight="1.2" type="type2" color="authLabelColor" upper fontWeight="bold" mt={4}>
                Dados cadastrais
              </Typography>

              <Typography lineHeight="1.4" type="type2" color="authLabelColor" mt={1}>
                {userDetails.name}
                <br />
                {userDetails.contactEmail}
                <br />
                {phoneMask(userDetails.contactPhone)}
              </Typography>
            </Flex>
          </Flex>

          <Hr color="gray_600" />

          <Flex flexDirection="column" px={{ m: 30, d: 45 }}>
            {/* taxId */}
            <Flex flexDirection="column" mt={{ m: 20, d: 30 }}>
              <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                CPF:
              </Typography>

              <Box position="relative" mt={2}>
                <AuthInput2
                  name="taxId"
                  type="text"
                  onChange={formikHandleChange}
                  onFocus={formikHandleFocus}
                  onBlur={formikHandleBlur}
                  value={cpfMask(formik.values.taxId)}
                  data-haserror={formik.touched.taxId && formik.errors.taxId}
                  tabIndex={1}
                />
              </Box>

              {formik.touched.taxId && formik.errors.taxId ? <HelperText type="error">{formik.errors.taxId}</HelperText> : null}
            </Flex>
            {/* /taxId */}

            {/* birthDate */}
            <Flex flexDirection="column" mt={{ m: 20 }}>
              <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                Data de Nascimento:
              </Typography>

              <Box position="relative" mt={2}>
                <AuthInput2
                  name="birthDate"
                  type="text"
                  onChange={formikHandleChange}
                  onFocus={formikHandleFocus}
                  onBlur={formikHandleBlur}
                  value={dateMask(formik.values.birthDate)}
                  data-haserror={formik.touched.birthDate && formik.errors.birthDate}
                  tabIndex={1}
                  maxLength={10}
                />
              </Box>

              {formik.touched.birthDate && formik.errors.birthDate ? <HelperText type="error">{formik.errors.birthDate}</HelperText> : null}
            </Flex>
            {/* /birthDate */}

            {/* password */}
            <Flex flexDirection="column" mt={{ m: 20, d: 30 }}>
              <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                Senha de acesso:
              </Typography>

              <Box position="relative" mt={2}>
                <ButtonShowPassword
                  onClick={() => toggleShowPassword((prev) => !prev)}
                  type="button"
                  icon={showPassword ? 'eyeOff' : 'eye'}
                  tabIndex={3}
                  color="primary_contrast"
                  bg="transparent"
                  iconWidth={22}
                />

                <AuthInput2
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  onChange={formikHandleChange}
                  onFocus={formikHandleFocus}
                  onBlur={formikHandleBlur}
                  value={formik.values.password}
                  data-haserror={formik.touched.password && formik.errors.password}
                  tabIndex={2}
                  style={{ paddingRight: 50 }}
                />
              </Box>

              {
                formik.touched.password && formik.errors.password && <HelperText type="error">{formik.errors.password}</HelperText>
                //  : (
                //   <Typography type="type0" color="gray_500" lineHeight={1.4} mt={3}>
                //     Por segurança, sua senha deve conter no mínimo 8 caracteres sendo pelo menos 1
                //     letra maiúscula, 1 letra minúscula, 1 número e um caractere especial (%?!@#$&*).
                //   </Typography>
                // )
              }
            </Flex>
            {/* /password */}
          </Flex>

          <Flex mt={5}>
            <AuthButton type="submit">Cadastrar e ir para o login</AuthButton>
          </Flex>
        </Flex>
      </form>
    </Page>
  ) : (
    <Flex flexDirection="column">
      <Flex flexDirection="column" p={{ m: 30, d: 45 }}>
        <Typography type="type4" color="authLabelColor" fontWeight="bold">
          A página que você está tentando acessar, não existe mais.
        </Typography>

        <Flex mt={3}>
          <Link tabIndex={4} to={createUrl('login')}>
            <Typography type="type1" color="authLabelColor" colorHover="primary_contrast" upper>
              Ir para o login
            </Typography>
          </Link>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default ResetPasswordView;
